import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Building2, Users, Plus, Search, GripVertical, Trash2 } from "lucide-react";
import { EmployeeWithProfile } from "@/pages/OrgChartPage";
import { Department } from "./types";

interface OrgChartSidebarProps {
  departments: Department[];
  employees: EmployeeWithProfile[];
  itemsOnCanvas: Set<string>;
  onDragStart: (e: React.DragEvent, type: 'department' | 'employee', data: Department | EmployeeWithProfile) => void;
  onAddDepartment: (name: string) => Promise<void>;
  onDeleteDepartment: (dept: Department) => void;
}

export function OrgChartSidebar({
  departments, employees, itemsOnCanvas,
  onDragStart, onAddDepartment, onDeleteDepartment
}: OrgChartSidebarProps) {
  const [search, setSearch] = useState("");
  const [newDeptName, setNewDeptName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const searchLower = search.toLowerCase();

  const sidebarDepartments = useMemo(
    () => departments.filter(d => !itemsOnCanvas.has(d.id) && d.name.toLowerCase().includes(searchLower)),
    [departments, itemsOnCanvas, searchLower]
  );

  const sidebarEmployees = useMemo(
    () => employees.filter(e => !itemsOnCanvas.has(e.id) && (
      e.name.toLowerCase().includes(searchLower) ||
      e.position.toLowerCase().includes(searchLower) ||
      (e.department || '').toLowerCase().includes(searchLower)
    )),
    [employees, itemsOnCanvas, searchLower]
  );

  const handleAdd = async () => {
    if (!newDeptName.trim()) return;
    await onAddDepartment(newDeptName.trim());
    setNewDeptName("");
    setIsDialogOpen(false);
  };

  return (
    <Card className="w-72 flex-shrink-0 border-0 shadow-xl bg-card/95 backdrop-blur-sm rounded-2xl overflow-hidden">
      <CardHeader className="pb-3 space-y-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="font-semibold tracking-tight">Elemente</span>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neue Abteilung</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Name der Abteilung"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
                <Button onClick={handleAdd} className="w-full">Erstellen</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs rounded-lg bg-muted/50 border-0 focus-visible:ring-1"
          />
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <ScrollArea className="h-[calc(100vh-340px)]">
          {/* Departments */}
          <div className="mb-5">
            <div className="flex items-center gap-2 px-1 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              <Building2 className="h-3 w-3" />
              Abteilungen
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5 rounded-full">{sidebarDepartments.length}</Badge>
            </div>
            <div className="space-y-1.5 mt-1">
              {sidebarDepartments.map(dept => (
                <div
                  key={dept.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, 'department', dept)}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-primary/8 border border-primary/15 cursor-grab hover:bg-primary/15 hover:border-primary/30 hover:shadow-sm transition-all group active:cursor-grabbing active:scale-[0.98]"
                >
                  <GripVertical className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground/70" />
                  <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-xs flex-1 truncate font-medium">{dept.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteDepartment(dept); }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/15 rounded-lg transition-all"
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </button>
                </div>
              ))}
              {sidebarDepartments.length === 0 && (
                <p className="text-[11px] text-muted-foreground/60 px-2 py-3 text-center italic">
                  {search ? "Keine Treffer" : "Alle auf dem Canvas"}
                </p>
              )}
            </div>
          </div>

          {/* Employees */}
          <div>
            <div className="flex items-center gap-2 px-1 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              <Users className="h-3 w-3" />
              Mitarbeiter
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5 rounded-full">{sidebarEmployees.length}</Badge>
            </div>
            <div className="space-y-1 mt-1">
              {sidebarEmployees.map(emp => (
                <div
                  key={emp.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, 'employee', emp)}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-card border border-border/50 cursor-grab hover:shadow-md hover:border-primary/30 transition-all active:cursor-grabbing active:scale-[0.98]"
                >
                  <GripVertical className="h-3 w-3 text-muted-foreground/40" />
                  <Avatar className="h-6 w-6 ring-1 ring-border/50">
                    <AvatarImage src={emp.profile_image_url || undefined} />
                    <AvatarFallback className="text-[8px] bg-muted font-medium">
                      {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate font-medium">{emp.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{emp.position}</p>
                  </div>
                </div>
              ))}
              {sidebarEmployees.length === 0 && (
                <p className="text-[11px] text-muted-foreground/60 px-2 py-3 text-center italic">
                  {search ? "Keine Treffer" : "Alle auf dem Canvas"}
                </p>
              )}
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
